import { useMemo } from 'react';
import SelectMonth from 'components/widgets/form/SelectMonth';
import SelectYear from 'components/widgets/form/SelectYear';
import { Col, Row, Button } from 'reactstrap';
import useClientOrders from 'views/orders/useClientOrders';
import { TableResponsive } from 'components/table/tanstack/responsive/TableResponsive';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import ClientRequiredContent from 'components/ClientRequiredContent';
import ViewPage from 'components/ViewPage';
import IconButton from 'components/form/IconButton';
import { Link } from 'react-router-dom';
import { Filter, PlusSquare, X, Eye } from 'react-feather';
import FormInput from 'components/form/FormInput';
import FlexRowCol from 'components/FlexRowCol';

const ViewClientOrders = () => {
  const {
    year,
    month,
    setSelMonth,
    setSelYear,
    searchOrders,
    ordersQuery,
    npes,
    addNpe,
    removeNpe,
    npeInput,
    setNpeInput,
  } = useClientOrders();

  const { orders, from, to } = ordersQuery;
  const { t, i18n } = useTranslation('common');

  const handleAddNpe = () => {
    if (npeInput) {
      addNpe(npeInput);
      setNpeInput('');
    }
  };

  const invalidNpe = npeInput !== '' && Number.isNaN(parseInt(npeInput, 10));

  const columns = useMemo(() => {
    return [
      { accessorKey: 'npe', header: t('order.num'), sortingFn: 'alphanumeric' },
      { accessorKey: 'date', header: t('order.date'), sortingFn: 'alphanumeric' },
      { accessorKey: 'entrega', header: t('order.delDate'), sortingFn: 'alphanumeric' },
      {
        accessorKey: 'situacio',
        header: t('order.state'),
        cell: ({ getValue }) => {
          const value = getValue();
          if (value === '0') {
            return t('order.status.pending');
          }
          if (value === '1') {
            return t('order.status.delivered');
          }
          return '-';
        },

        sortingFn: 'alphanumeric',
      },
      {
        accessorKey: 'importeNet',
        header: t('order.totalNet'),
        cell: ({ getValue }) => {
          const importeNet = getValue();
          return `${importeNet.toFixed(2)}€`;
        },
        sortingFn: 'alphanumeric',
      },
      { accessorKey: 'repre.nom', header: t('order.repre'), sortingFn: 'alphanumeric' },
      { accessorKey: 'marca', header: t('brand'), sortingFn: 'text' },
      {
        header: t('action'),
        accessorKey: 'view',
        accessorFn: (originalRow) => originalRow.npe,

        cell: ({ getValue }) => <LinkOrder npe={getValue()} />,
      },
    ];
  }, [i18n.language]);

  const data = useMemo(() => {
    return orders;
  }, [orders]);

  const LinkOrder = ({ npe }) => {
    LinkOrder.propTypes = {
      npe: PropTypes.number,
    };
    return (
      <div className="d-flex justify-content-center">
        <Link to={`/order/${npe}`}>
          <span role="button" title={t('order.tooltipView')}>
            <Eye />
          </span>
        </Link>
      </div>
    );
  };

  return (
    <ClientRequiredContent>
      <ViewPage title={t('page.orders.title')}>
        <Row>
          <Col xs={6} md={4} className="mb-4">
            <SelectYear onChange={(e) => setSelYear(e.value)} valueYear={year} limit={25} />
          </Col>
          <Col xs={6} md={4}>
            <SelectMonth defaultValue={0} onChange={(e) => setSelMonth(e.value)} value={month} />
          </Col>
          <FlexRowCol media="(max-width: 860px)" gap={{ row: 3, col: 0 }}>
            <div className="d-flex gap-3 align-items-center">
              <div className="justify-content-center">
                <FormInput
                  label={t('filterByNpe')}
                  onChange={(e) => setNpeInput(e.target.value)}
                  decimals={0}
                  error={invalidNpe ? 'Invalid' : ''}
                  value={npeInput}
                />
              </div>
              <div className="mt-2">
                <IconButton
                  icon={
                    <>
                      <PlusSquare />
                      <Filter />
                    </>
                  }
                  onClick={handleAddNpe}
                  disabled={invalidNpe}
                />
              </div>
            </div>
            <div>
              <div>
                <h5>{t('selectedNpes')}</h5>
              </div>
              <div className="d-flex gap-3 align-items-center">
                {npes.map((npe, index) => {
                  return (
                    <div
                      key={npe}
                      role="button"
                      tabIndex={index}
                      className="d-flex flex-row px-2 py-1 gap-1 text-white bg-success"
                      onClick={() => removeNpe(npe)}
                    >
                      <div>{npe}</div>
                      <div className="align-self-start">
                        <X size={14} />
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </FlexRowCol>
        </Row>
        <Row>
          <Col xs={12} md={4}>
            <Button
              className="w-100"
              color="primary"
              onClick={(e) => {
                searchOrders(e);
              }}
            >
              {t('buttons.search')}
            </Button>
          </Col>
        </Row>
        <div className="mt-4">
          <TableResponsive columns={columns} data={data} pageSize={10} />
        </div>
      </ViewPage>
    </ClientRequiredContent>
  );
};

export default ViewClientOrders;
